import React, { useState, useEffect, useRef } from "react";
import { IoIosAddCircleOutline, IoMdClose } from "react-icons/io";
import { SlOptionsVertical } from "react-icons/sl";
import Swal from "sweetalert2";

import {
  ButtonAction,
  MenuTable,
  MenuTableDivider,
  MenuTableItem,
  Table,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
  TableContainer,
} from "../../../components/Table/styles";
import {
  BlueTitle,
  GrayTitle,
  ModalBody,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
  StyledHR,
  TextError,
  TitleAndButton,
} from "../../../shared/styles/global.styles";
import { Colors } from "../../../shared/colors/colors";
import formaPagamentoService from "../../../services/FormaPagamentoService";
import { Col, Form, Row } from "react-bootstrap";
import { Label } from "../../../components/Label/styles";
import InputComponent from "../../../components/Input/input";
import { FormaPagamentoDto } from "../../../shared/dtos/FormaPagamento.dto";
import Button from "../../../components/Button/button";
import {
  ContainerButton,
  ContainerFormaPagamento,
  MainContainerFormaPagamento,
  SmallText,
} from "./formaPagamento.styles";
import { useAuth } from "../../../AuthContext/AuthContext";
import { ModulosEnum } from "../../../shared/enum/modulos.enum";
import { PermissoesEnum } from "../../../shared/enum/permicao.enum";
import { IoCloseSharp } from "react-icons/io5";

const FormaPagamento: React.FC = () => {
  const { checkPermission } = useAuth();
  const editOnly = checkPermission(
    ModulosEnum.FORMA_PAGAMENTO,
    PermissoesEnum.ESCRITA
  );
  const [formaPagamento, setFormaPagamento] = useState<FormaPagamentoDto[]>([]);
  const [inputFormaPagamento, setInputFormaPagamento] =
    useState<FormaPagamentoDto>({
      nome: "",
      descricao: "",
    });
  const [selectedFormaPagamento, setSelectedFormaPagamento] =
    useState<FormaPagamentoDto | null>(null);
  const [isRequest, setIsRequest] = useState(false);
  const [isOpenDropdown, setIsOpenDropdown] = useState<number | null>(null);
  const [showModalNewFormaPagamento, setShowModalNewFormaPagamento] =
    useState<boolean>(false);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);

  const getFormaPagamento = async () => {
    const response = await formaPagamentoService.getAllFormaPagamento();
    setFormaPagamento(response);
  };

  const handleSubmit = () => {
    if (inputFormaPagamento?.nome.trim() === "") {
      Swal.fire("Alerta", "O nome não pode ser enviada vazia.", "warning");
      setInvalidFields(["nomeFormaPagamento"]);
      return;
    }

    const formaPagamentoExists = formaPagamento.find(
      (l) =>
        l.nome.trim().toLowerCase() ===
        inputFormaPagamento?.nome.trim().toLowerCase()
    );
    if (formaPagamentoExists) {
      Swal.fire(
        "Alerta",
        "Já existe uma forma de pagamento com esse nome cadastrado.",
        "warning"
      );
      return;
    }

    setIsRequest(true);

    Swal.fire({
      title: "Aguarde...",
      text: "Salvando forma de pagamento...",
      icon: "info",
      showConfirmButton: false,
      allowOutsideClick: false,
      willOpen: () => {
        Swal.showLoading();
      },
    });

    if (selectedFormaPagamento) {
      formaPagamentoService
        .updateFormaPagamento(
          selectedFormaPagamento.id as number,
          inputFormaPagamento as FormaPagamentoDto
        )
        .then((response) => {
          Swal.close();
          Swal.fire(
            "Sucesso",
            "Forma de Pagamento atualizada com sucesso!",
            "success"
          );
          getFormaPagamento(); // Update the table after update
        })
        .catch((error) => {
          console.error(
            "[handleUpdate] Erro ao atualizar formaPagamento:",
            error
          );
          Swal.close();
          Swal.fire(
            "Erro",
            error instanceof Error
              ? error.message
              : "Erro desconhecido ao atualizar formaPagamento.",
            "error"
          );
        });
    } else {
      formaPagamentoService
        .createFormaPagamento(inputFormaPagamento as FormaPagamentoDto)
        .then((response) => {
          Swal.close();
          Swal.fire(
            "Sucesso",
            "Forma de Pagamento criado com sucesso!",
            "success"
          );
          getFormaPagamento(); // Update the table after creation
        })
        .catch((error) => {
          console.error("[handleAdd] Erro ao criar formaPagamento:", error);
          Swal.close();
          Swal.fire(
            "Erro",
            error instanceof Error
              ? error.message
              : "Erro desconhecido ao criar formaPagamento.",
            "error"
          );
        });
    }

    handleClear();
    setIsRequest(false);
    setShowModalNewFormaPagamento(false);
  };

  const handleDeleteFormaPagamento = async (id: number) => {
    try {
      Swal.fire({
        title: "Aguarde...",
        text: "Excluindo a forma de pagamento...",
        icon: "info",
        showConfirmButton: false,
        allowOutsideClick: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });

      const result = await Swal.fire({
        title: "Tem certeza?",
        text: "Você realmente deseja excluir esta forma de pagamento? Esta ação não pode ser desfeita.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sim, excluir!",
        cancelButtonText: "Cancelar",
        allowOutsideClick: false,
      });

      if (result.isConfirmed) {
        await formaPagamentoService.deleteFormaPagamento(id);
        getFormaPagamento(); // Refresh the list after deletion
        Swal.close();
        Swal.fire(
          "Excluído!",
          "A forma de pagamento foi excluída com sucesso.",
          "success"
        );
      } else {
        Swal.close();
      }
    } catch (error) {
      console.error(`[handleDelete] Erro ao excluir ID ${id}:`, error);
      Swal.close();
      Swal.fire(
        "Erro",
        error instanceof Error
          ? error.message
          : "Erro desconhecido ao excluir item.",
        "error"
      );
    }
  };

  const handleClear = () => {
    setInputFormaPagamento({ nome: "" });
    setSelectedFormaPagamento(null);
  };

  const toggleDropdown = (id: number) => {
    setIsOpenDropdown(id === isOpenDropdown ? null : id);
  };

  const closeNewFormaPagamentoModal = () => {
    setSelectedFormaPagamento(null);
    setShowModalNewFormaPagamento(false);
    setIsRequest(false);
  };

  const clearError = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = event.target;

    setInvalidFields((prev) => prev.filter((field) => field !== name));
  };

  useEffect(() => {
    getFormaPagamento();
  }, []);
  const menuRefsMap = useRef<Map<number, HTMLDivElement | null>>(new Map());
  return (
    <MainContainerFormaPagamento>
      <TitleAndButton>
        <GrayTitle>Forma de Pagamento</GrayTitle>
        <ContainerButton>
          {editOnly && (
            <Button
              schemaButton="full-primary"
              action={() => {
                setShowModalNewFormaPagamento(true);
                setInputFormaPagamento({ nome: "", descricao: "" });
              }}
              icon={
                <IoIosAddCircleOutline
                  style={{ fontSize: "1.3rem", marginRight: "0.3rem" }}
                />
              }
              disabled={isRequest && !editOnly}
            >
              Criar nova forma de pagamento
            </Button>
          )}
        </ContainerButton>
      </TitleAndButton>
      <StyledHR />
      <TableContainer style={{ flex: 1 }}>
        <Table id="formaPagamento-table">
          <TableHead>
            <TableRow>
              <TableHeader>Forma de pagamento</TableHeader>
              <TableHeader>Descrição</TableHeader>
              {editOnly && (
                <TableHeader style={{ width: "1px", whiteSpace: "nowrap" }}>
                  Ações
                </TableHeader>
              )}
            </TableRow>
          </TableHead>
          <tbody>
            {formaPagamento.length > 0 ? (
              formaPagamento.map((data) => (
                <TableRow key={data.id}>
                  <TableData>{data.nome}</TableData>
                  <TableData>{data.descricao}</TableData>
                  {editOnly && (
                    <TableData
                      key={`actions-${data.id}`}
                      style={{
                        position: "relative",
                        overflow: "visible",
                      }}
                    >
                      <ButtonAction onClick={() => toggleDropdown(data.id!)}>
                        {isOpenDropdown === data.id ? (
                          <IoMdClose
                            cursor={"pointer"}
                            size={20}
                            style={{ color: Colors.primary }}
                          />
                        ) : (
                          <SlOptionsVertical
                            cursor={"pointer"}
                            size={20}
                            style={{ color: Colors.primary }}
                          />
                        )}
                      </ButtonAction>
                      <MenuTable
                        show={isOpenDropdown === data.id}
                        align="start"
                        isLastRows={
                          formaPagamento.length > 0 &&
                          formaPagamento.indexOf(data) >=
                            formaPagamento.length -
                              Math.ceil(formaPagamento.length / 2)
                        }
                        ref={(el: HTMLDivElement | null) => {
                          // Armazena a referência no mapa usando o ID do registro como chave
                          menuRefsMap.current.set(data.id!, el);
                        }}
                      >
                        <MenuTableItem
                          eventKey="1"
                          onClick={() => {
                            setSelectedFormaPagamento(data);
                            setInputFormaPagamento(data);
                            setIsOpenDropdown(null);
                            setShowModalNewFormaPagamento(true);
                          }}
                        >
                          Editar
                        </MenuTableItem>
                        <MenuTableDivider />
                        <MenuTableItem
                          eventKey="2"
                          onClick={() => {
                            handleDeleteFormaPagamento(data.id!);
                            setIsOpenDropdown(null);
                          }}
                        >
                          Excluir
                        </MenuTableItem>
                      </MenuTable>
                    </TableData>
                  )}
                </TableRow>
              ))
            ) : (
              <tr>
                <TableData colSpan={2} className="text-center">
                  Sem lotação encontrada.
                </TableData>
              </tr>
            )}
          </tbody>
        </Table>
      </TableContainer>

      <ModalContainer show={showModalNewFormaPagamento}>
        <ModalContent width={35}>
          <Button
            type="button"
            schemaButton="x-close"
            action={() => closeNewFormaPagamentoModal()}
            icon={<IoCloseSharp size={20} />}
            position="absolute"
            top={10}
            right={10}
          />
          <ModalHeader>
            <BlueTitle>
              {!!selectedFormaPagamento ? "Editar " : "Cadastrar nova "}
              forma de pagamento
            </BlueTitle>
          </ModalHeader>
          <ModalBody>
            <Label htmlFor="tipoRemessa">
              Forma de pagamento <TextError>*</TextError>
            </Label>
            <div>
              <InputComponent
                type="text"
                name="descricaoFormaPagamento"
                id="descricaoFormaPagamento"
                value={inputFormaPagamento?.nome ?? ""}
                onChange={(e) => {
                  if (e.target.value.length <= 50) {
                    setInputFormaPagamento({
                      ...inputFormaPagamento,
                      nome: e.target.value,
                    });
                    clearError(e);
                  }
                }}
                placeholder="Nome da forma de pagamento"
                disabled={isRequest}
                color="black"
                width={Number("100%")}
                maxLength={50}
                errorMessage={
                  invalidFields.includes("nomeFormaPagamento")
                    ? "Nome da forma de pagamento é obrigatório"
                    : ""
                }
              />
              <SmallText style={{ textAlign: "right", marginTop: "4px" }}>
                Total de caracteres {inputFormaPagamento?.nome?.length ?? 0}/50
              </SmallText>
            </div>
            <Label htmlFor="tipoRemessa">Descrição</Label>
            <div>
              <Form.Control
                as="textarea"
                style={{
                  minHeight: "100px",
                  maxHeight: "300px",
                  overflow: "auto",
                }}
                placeholder="Descrição (máximo 200 caracteres)"
                name="descricaoFormaPagamento"
                id="descricaoFormaPagamento"
                value={inputFormaPagamento?.descricao ?? ""}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 200);
                  setInputFormaPagamento({
                    ...inputFormaPagamento,
                    descricao: value,
                  });
                }}
                disabled={isRequest}
                rows={3}
                maxLength={100}
              />
              <SmallText style={{ textAlign: "right", marginTop: "4px" }}>
                Total de caracteres{" "}
                {inputFormaPagamento?.descricao?.length ?? 0}/200
              </SmallText>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              schemaButton="full-primary"
              action={() => {
                handleSubmit();
              }}
              disabled={isRequest || inputFormaPagamento?.nome === ""}
            >
              {!!selectedFormaPagamento ? "Salva edição" : "Cadastrar nova"}
              forma de pagamento
            </Button>
          </ModalFooter>
        </ModalContent>
      </ModalContainer>
    </MainContainerFormaPagamento>
  );
};

export default FormaPagamento;
